import React, { useEffect, useRef, useState, type JSX } from "react";
import { ChevronDownIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import LogoutButton from "../auth/LogoutButton";

const CVC_BLUE = "#234E81";
const INLINE_DEALS_FLAG = "ff_inlineDeals";

function inlineDealsEnabled(): boolean {
  return localStorage.getItem(INLINE_DEALS_FLAG) === "true";
}

/** Persisted in localStorage and read at boot, so flipping it reloads the app. */
function InlineDealsToggle(): JSX.Element {
  const enabled = inlineDealsEnabled();

  return (
    <div className="px-4 py-3 border-b border-gray-200">
      <label className="flex items-center justify-between cursor-pointer">
        <span className="text-xs text-gray-600">Inline Deals</span>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => {
            localStorage.setItem(INLINE_DEALS_FLAG, enabled ? "false" : "true");
            window.location.reload();
          }}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
            enabled ? "bg-cvc-blue" : "bg-gray-300"
          }`}
        >
          <span
            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
              enabled ? "translate-x-4" : "translate-x-0.5"
            }`}
          />
        </button>
      </label>
    </div>
  );
}

function UserDropdown({
  userName,
  userEmail,
}: {
  userName: string | null;
  userEmail: string | null;
}): JSX.Element {
  return (
    <div className="absolute right-0 mt-2 w-64 origin-top-right bg-white rounded-lg shadow-lg ring-1 ring-black/5 focus:outline-hidden z-50">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <UserCircleIcon className="h-10 w-10 text-gray-400" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{userName || "User"}</p>
            <p className="text-sm text-gray-500 truncate">{userEmail || "No email"}</p>
          </div>
        </div>
      </div>
      <InlineDealsToggle />
      <div className="py-1">
        <div className="px-4 py-2">
          <LogoutButton />
        </div>
      </div>
    </div>
  );
}

export default function HeaderUserMenu({
  userName,
  userEmail,
}: {
  userName: string | null;
  userEmail: string | null;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
        aria-label="User menu"
      >
        <UserCircleIcon className="h-7 w-7" style={{ color: CVC_BLUE }} />
        <ChevronDownIcon
          className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          style={{ color: CVC_BLUE }}
        />
      </button>

      {isOpen && <UserDropdown userName={userName} userEmail={userEmail} />}
    </div>
  );
}
